<script setup lang="ts">
import { computed } from 'vue'
import type { Tone } from '../tokens'
import { toneVars } from '../tone-vars'

/**
 * 键值对展示。**替代所有裸 `<table>` 的键值对**。
 *
 * 为什么值得单独立一个组件:裸表格是这次改造里最扎眼的一处「开发草稿感」——
 * 它没有列宽约束,长字符串会把布局撑开,而短字段又挤在一起。
 * 这里统一成「标签 12px 灰 / 值 13.5px 深」的固定节奏,扫读时才成列。
 *
 * 空值统一渲染成灰色的破折号,而不是留白:留白读起来像「这里没有这一项」,
 * 而破折号说的是「这一项在,但没有值」—— 后者才是接口的返回情况。
 */
interface FieldItem {
  readonly label: string
  readonly value?: string | number | null
  /** 单号、ID、JSON 这类要用等宽字体读的值 */
  readonly mono?: boolean
  /** 跨列 —— 长文本(货品说明、备注)用它 */
  readonly span?: 1 | 2
}

/**
 * `inline`(S-42 加):名与值在**同一行**(名左、值右),而不是名在上、值在下。
 *
 * 画板 O1 的抽屉里两种排法同时出现,而且不是随手画的:订单概览那张卡是
 * 「名在上、值在下」(行高 40,给的是扫读一列字段);那张高亮的新字段卡是
 * 「名左、值右」(行高 19,四行挤在一条 113 高的卡里)。
 * 两种都是键值对展示 —— 与其在页面里再手写一份 `display:flex` 的行,
 * 不如把它做成这一层的第二个档:键值对只有这一个组件,改版式只改一处。
 */
const props = withDefaults(
  defineProps<{ items: readonly FieldItem[]; columns?: 1 | 2 | 3; tone?: Tone; inline?: boolean }>(),
  { columns: 2, tone: 'light', inline: false },
)

const vars = computed(() => toneVars(props.tone))

function isBlank(value: string | number | null | undefined): boolean {
  return value === null || value === undefined || value === ''
}
</script>

<template>
  <dl class="grid" :class="[`cols-${props.columns}`, { inline: props.inline }]" :style="vars">
    <div v-for="(item, index) in props.items" :key="index" class="item" :class="{ wide: item.span === 2 }">
      <dt class="label">{{ item.label }}</dt>
      <dd class="value" :class="{ mono: item.mono, blank: isBlank(item.value) }">
        {{ isBlank(item.value) ? '—' : item.value }}
      </dd>
    </div>
  </dl>
</template>

<style scoped>
.grid {
  display: grid;
  gap: 12px 24px;
  margin: 0;
}

.cols-1 {
  grid-template-columns: 1fr;
}

.cols-2 {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.cols-3 {
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.item {
  min-width: 0;
}

/* 名左值右那一档:一行一条(画板 O1 的新字段卡),值贴该列的右边缘 */
.inline {
  gap: 12px 16px;
}

.inline .item {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
}

.inline .label {
  margin-bottom: 0;
}

/* 跨列:长文本需要整行的宽度,挤在半列里会折成一团 */
.wide {
  grid-column: 1 / -1;
}

.label {
  font-size: 12px;
  color: var(--sd-text3);
  margin-bottom: 3px;
}

.value {
  margin: 0;
  font-size: 13.5px;
  line-height: 1.6;
  color: var(--sd-text1);
  word-break: break-word;
  font-variant-numeric: tabular-nums;
}

.mono {
  font-family: var(--sd-font-mono);
  font-size: 13px;
}

.blank {
  color: var(--sd-text3);
}
</style>
